/**
 * Exact rational arithmetic.
 *
 * Every fraction the platform touches — student answers, generated exercise
 * parameters, worked-example steps — flows through this type. Floating point is
 * never used to decide whether an answer is right, because `0.1 + 0.2 !== 0.3`
 * is not a lesson we want to teach a nine-year-old.
 */

export type Fraction = {
  /** Numerator. Carries the sign of the fraction. */
  readonly n: number;
  /** Denominator. Always a positive integer. */
  readonly d: number;
};

export function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x;
}

export function lcm(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return Math.abs((a / gcd(a, b)) * b);
}

/** Builds a fraction in lowest terms with a positive denominator. */
export function fraction(n: number, d = 1): Fraction {
  if (d === 0) throw new Error('Denominator cannot be zero');
  if (!Number.isInteger(n) || !Number.isInteger(d)) {
    throw new Error(`Fraction requires integers, received ${n}/${d}`);
  }
  const sign = d < 0 ? -1 : 1;
  const divisor = gcd(n, d) || 1;
  return { n: (sign * n) / divisor, d: (sign * d) / divisor };
}

/** Builds a fraction without reducing it — needed to teach equivalence. */
export function rawFraction(n: number, d: number): Fraction {
  if (d === 0) throw new Error('Denominator cannot be zero');
  return d < 0 ? { n: -n, d: -d } : { n, d };
}

export const isWhole = (f: Fraction): boolean => f.n % f.d === 0;
export const isProper = (f: Fraction): boolean => Math.abs(f.n) < f.d;
export const toNumber = (f: Fraction): number => f.n / f.d;
export const simplify = (f: Fraction): Fraction => fraction(f.n, f.d);
export const neg = (f: Fraction): Fraction => rawFraction(-f.n, f.d);

export const add = (a: Fraction, b: Fraction): Fraction => fraction(a.n * b.d + b.n * a.d, a.d * b.d);
export const sub = (a: Fraction, b: Fraction): Fraction => add(a, neg(b));
export const mul = (a: Fraction, b: Fraction): Fraction => fraction(a.n * b.n, a.d * b.d);

export function div(a: Fraction, b: Fraction): Fraction {
  if (b.n === 0) throw new Error('Cannot divide by zero');
  return fraction(a.n * b.d, a.d * b.n);
}

/** Sign of `a - b`: negative, zero, or positive. */
export function compare(a: Fraction, b: Fraction): number {
  return Math.sign(a.n * b.d - b.n * a.d);
}

/** Same value, regardless of how each fraction is written. `2/4 ≡ 1/2`. */
export const equals = (a: Fraction, b: Fraction): boolean => compare(a, b) === 0;

/** Same value *and* same written form. `2/4` is not identical to `1/2`. */
export const identical = (a: Fraction, b: Fraction): boolean => a.n === b.n && a.d === b.d;

export const isSimplified = (f: Fraction): boolean => gcd(f.n, f.d) === 1;

export type MixedNumber = { whole: number; part: Fraction };

/** `7/3 → 2 1/3`. The sign lives on `whole` unless the whole part is zero. */
export function toMixed(f: Fraction): MixedNumber {
  const sign = f.n < 0 ? -1 : 1;
  const absN = Math.abs(f.n);
  const whole = Math.floor(absN / f.d);
  const remainder = absN % f.d;
  return {
    whole: sign * whole,
    part: rawFraction(whole === 0 ? sign * remainder : remainder, f.d),
  };
}

export function fromMixed(whole: number, n: number, d: number): Fraction {
  const sign = whole < 0 ? -1 : 1;
  return fraction(sign * (Math.abs(whole) * d + n), d);
}

/** Exact conversion of a terminating decimal. `0.75 → 3/4`. */
export function fromDecimal(value: number): Fraction {
  if (Number.isInteger(value)) return fraction(value, 1);
  const text = value.toString();
  if (text.includes('e') || text.includes('E')) {
    throw new Error(`Cannot exactly convert ${text} to a fraction`);
  }
  const decimals = text.split('.')[1]?.length ?? 0;
  const scale = 10 ** decimals;
  return fraction(Math.round(value * scale), scale);
}

/**
 * Parses the forms a student can plausibly type: `3/4`, `-3/4`, `1 1/2`,
 * `1_1/2`, `5`, `0.75`. Returns `null` when the text is not a number at all,
 * so callers can distinguish "wrong" from "unparseable".
 */
export function parseFraction(input: string): Fraction | null {
  const text = input.trim().replace(/\s+/g, ' ');
  if (text === '') return null;

  const mixed = /^(-?\d+)[\s_]+(\d+)\s*\/\s*(\d+)$/.exec(text);
  if (mixed) {
    const [, whole, n, d] = mixed as unknown as [string, string, string, string];
    if (Number(d) === 0) return null;
    return fromMixed(Number(whole), Number(n), Number(d));
  }

  const simple = /^(-?\d+)\s*\/\s*(-?\d+)$/.exec(text);
  if (simple) {
    const [, n, d] = simple as unknown as [string, string, string];
    if (Number(d) === 0) return null;
    return fraction(Number(n), Number(d));
  }

  if (/^-?\d+$/.test(text)) return fraction(Number(text), 1);
  if (/^-?\d*\.\d+$/.test(text)) return fromDecimal(Number(text));

  return null;
}

/** Same as {@link parseFraction} but preserves `2/4` instead of reducing it. */
export function parseFractionExact(input: string): Fraction | null {
  const text = input.trim();
  const simple = /^(-?\d+)\s*\/\s*(-?\d+)$/.exec(text);
  if (simple) {
    const [, n, d] = simple as unknown as [string, string, string];
    if (Number(d) === 0) return null;
    return rawFraction(Number(n), Number(d));
  }
  return parseFraction(input);
}

export function toLatex(f: Fraction, options: { mixed?: boolean } = {}): string {
  if (f.d === 1) return String(f.n);
  if (options.mixed && Math.abs(f.n) > f.d) {
    const { whole, part } = toMixed(f);
    return `${whole}\\tfrac{${Math.abs(part.n)}}{${part.d}}`;
  }
  return f.n < 0 ? `-\\frac{${-f.n}}{${f.d}}` : `\\frac{${f.n}}{${f.d}}`;
}

export function toText(f: Fraction): string {
  return f.d === 1 ? String(f.n) : `${f.n}/${f.d}`;
}
